/**
 * validateDischargePayload.js
 *
 * Pure-function validator & auto-fixer for the discharge summary JSON payload.
 * Ensures the data shape is clean, consistent, and complete before PDF generation.
 *
 * Usage:
 *   const { valid, data, warnings } = validateDischargePayload(rawPayload);
 */

import { isLamaOrDama, defaultLamaDamaDetails } from "../constants/dischargeTypes";

// tiny helpers

const isNil = (v) => v === null || v === undefined;
const isBlankString = (v) => typeof v === "string" && v.trim() === "";

/** Deep-clone via structured clone (falls back to JSON round-trip). */
const deepClone = (obj) => {
  try {
    return structuredClone(obj);
  } catch {
    return JSON.parse(JSON.stringify(obj));
  }
};

/** Coerce a value to a trimmed string. */
const ensureString = (v) => {
  if (isNil(v)) return "";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v).trim();
};

/** Coerce a value to boolean. */
const ensureBool = (v) => {
  if (typeof v === "boolean") return v;
  if (typeof v === "string") {
    const lower = v.toLowerCase().trim();
    return ["true", "yes", "1"].includes(lower);
  }
  return Boolean(v);
};

/** Coerce a value to an array. Wraps solo values, nullifies nil. */
const ensureArray = (v) => {
  if (Array.isArray(v)) return v;
  if (isNil(v) || isBlankString(v)) return [];
  return [v];
};

/** Remove blank/empty strings from a string array. */
const stripBlanks = (arr) => arr.filter((item) => typeof item === "string" && item.trim() !== "");

/** Ensure an object exists, defaulting missing keys from a template. */
const ensureObject = (obj, defaults = {}) => {
  const base = obj && typeof obj === "object" && !Array.isArray(obj) ? obj : {};
  const merged = { ...defaults };
  for (const key of Object.keys(merged)) {
    if (key in base && !isNil(base[key])) {
      merged[key] = base[key];
    }
  }
  // Preserve extra keys from the source that aren't in the template
  for (const key of Object.keys(base)) {
    if (!(key in merged)) {
      merged[key] = base[key];
    }
  }
  return merged;
};

// medication item shape

const MEDICATION_DEFAULTS = {
  drug_name: "",
  dose: "",
  frequency: "",
  duration: "",
};

const fixMedicationItem = (item) => {
  if (!item || typeof item !== "object") return { ...MEDICATION_DEFAULTS };
  const fixed = { ...MEDICATION_DEFAULTS };
  for (const key of Object.keys(fixed)) {
    fixed[key] = ensureString(item[key]);
  }
  // Preserve extra keys (e.g. "purpose")
  for (const key of Object.keys(item)) {
    if (!(key in fixed)) {
      fixed[key] = ensureString(item[key]);
    }
  }
  return fixed;
};

// main validator

/**
 * Validates and auto-fixes a discharge summary payload.
 *
 * @param {object} rawData - The raw payload from the frontend form.
 * @returns {{ valid: boolean, data: object, warnings: string[] }}
 */
export const validateDischargePayload = (rawData) => {
  const warnings = [];

  if (!rawData || typeof rawData !== "object") {
    return {
      valid: false,
      data: {},
      warnings: ["Payload is empty or not an object."],
    };
  }

  const data = deepClone(rawData);

  // helper to log & fix
  const warn = (path, msg) => warnings.push(`${path}: ${msg}`);

  const fixStr = (obj, key, path) => {
    if (isNil(obj[key]) || (typeof obj[key] !== "string" && typeof obj[key] !== "number" && typeof obj[key] !== "boolean")) {
      if (!isNil(obj[key])) warn(path, `was ${typeof obj[key]}, coerced to string`);
      else warn(path, "was missing, set to empty string");
      obj[key] = ensureString(obj[key]);
    } else if (typeof obj[key] !== "string") {
      warn(path, `was ${typeof obj[key]}, coerced to string`);
      obj[key] = ensureString(obj[key]);
    }
  };

  // 1. document_type
  if (isNil(data.document_type)) {
    data.document_type = "Patient Discharge Summary";
    warn("document_type", "was missing, set to default");
  }

  // 2. hospital_details
  if (!data.hospital_details || typeof data.hospital_details !== "object") {
    data.hospital_details = {};
    warn("hospital_details", "was missing, created as empty object");
  }
  for (const key of ["name", "address", "contact"]) {
    fixStr(data.hospital_details, key, `hospital_details.${key}`);
  }

  // 3. header
  if (!data.header || typeof data.header !== "object") {
    data.header = {};
    warn("header", "was missing, created as empty object");
  }
  const headerStrFields = [
    "patient_id", "name", "age", "gender", "bed_no",
    "category", "consultant_incharge",
    "date_of_admission", "date_of_discharge", "discharge_type",
  ];
  for (const key of headerStrFields) {
    fixStr(data.header, key, `header.${key}`);
  }

  // header.discharge_details
  if (!data.header.discharge_details || typeof data.header.discharge_details !== "object") {
    data.header.discharge_details = {};
    warn("header.discharge_details", "was missing, created as empty object");
  }
  const dd = data.header.discharge_details;
  for (const key of ["condition_at_discharge", "mobility_status", "remarks"]) {
    fixStr(dd, key, `header.discharge_details.${key}`);
  }
  // follow_up_required → boolean
  if (typeof dd.follow_up_required !== "boolean") {
    warn("header.discharge_details.follow_up_required", `was ${typeof dd.follow_up_required}, coerced to boolean`);
    dd.follow_up_required = ensureBool(dd.follow_up_required);
  }

  // vital_signs
  dd.vital_signs = ensureObject(dd.vital_signs, { bp: "", pulse: "", temperature: "", spo2: "" });
  // respiratory_rate / consciousness arrive from the nurse's recorded vitals;
  // ensureObject already preserves keys outside its template, this just gives
  // them the same string coercion as the original four.
  for (const key of ["bp", "pulse", "temperature", "spo2", "respiratory_rate", "consciousness"]) {
    fixStr(dd.vital_signs, key, `header.discharge_details.vital_signs.${key}`);
  }

  // header.lama_dama_details
  if (isLamaOrDama(data.header.discharge_type)) {
    const defaults = defaultLamaDamaDetails(data.header);
    data.header.lama_dama_details = ensureObject(
      data.header.lama_dama_details,
      defaults
    );
    const ldd = data.header.lama_dama_details;
    for (const key of [
      "discharge_type",
      "reason",
      "reason_other",
      "counseled_by",
      "risks_explained_notes",
      "surrogate_name",
      "surrogate_relationship",
      "surrogate_contact",
      "condition_at_departure",
      "emergency_instructions",
      "declaration_text",
    ]) {
      fixStr(ldd, key, `header.lama_dama_details.${key}`);
    }
    ldd.risks_explained = ensureBool(ldd.risks_explained);
    ldd.can_return_anytime = ensureBool(ldd.can_return_anytime);
    ldd.declaration_acknowledged = ensureBool(ldd.declaration_acknowledged);
  }

  // 4. final_diagnosis
  if (!Array.isArray(data.final_diagnosis)) {
    data.final_diagnosis = ensureArray(data.final_diagnosis);
    warn("final_diagnosis", "was not an array, coerced");
  }
  data.final_diagnosis = stripBlanks(data.final_diagnosis);

  // 5. history
  if (!data.history || typeof data.history !== "object") {
    data.history = {};
    warn("history", "was missing, created as empty object");
  }
  fixStr(data.history, "history_of_present_illness", "history.history_of_present_illness");
  fixStr(data.history, "allergies", "history.allergies");
  fixStr(data.history, "family_history", "history.family_history");

  // past_medical_history, past_surgical_history → arrays
  for (const key of ["past_medical_history", "past_surgical_history"]) {
    if (!Array.isArray(data.history[key])) {
      data.history[key] = ensureArray(data.history[key]);
      warn(`history.${key}`, "was not an array, coerced");
    }
    data.history[key] = stripBlanks(data.history[key]);
  }

  // presenting_complaints → array
  if (!Array.isArray(data.history.presenting_complaints)) {
    data.history.presenting_complaints = ensureArray(data.history.presenting_complaints);
    warn("history.presenting_complaints", "was not an array, coerced");
  }
  data.history.presenting_complaints = stripBlanks(data.history.presenting_complaints);

  // personal_history
  data.history.personal_history = ensureObject(data.history.personal_history, {
    diet: "",
    smoking: "",
    alcohol: "",
  });
  for (const key of ["diet", "smoking", "alcohol"]) {
    fixStr(data.history.personal_history, key, `history.personal_history.${key}`);
  }

  // 6. treatment_given
  if (!data.treatment_given || typeof data.treatment_given !== "object") {
    data.treatment_given = {};
    warn("treatment_given", "was missing, created as empty object");
  }

  // procedures → array of strings
  if (!Array.isArray(data.treatment_given.procedures)) {
    data.treatment_given.procedures = ensureArray(data.treatment_given.procedures);
    warn("treatment_given.procedures", "was not an array, coerced");
  }
  data.treatment_given.procedures = stripBlanks(data.treatment_given.procedures);

  // medications_administered → array of objects
  if (!Array.isArray(data.treatment_given.medications_administered)) {
    data.treatment_given.medications_administered = ensureArray(data.treatment_given.medications_administered);
    warn("treatment_given.medications_administered", "was not an array, coerced");
  }
  data.treatment_given.medications_administered = data.treatment_given.medications_administered.map(fixMedicationItem);

  // supportive_care → array of strings
  if (!Array.isArray(data.treatment_given.supportive_care)) {
    data.treatment_given.supportive_care = ensureArray(data.treatment_given.supportive_care);
    warn("treatment_given.supportive_care", "was not an array, coerced");
  }
  data.treatment_given.supportive_care = stripBlanks(data.treatment_given.supportive_care);

  // 7. advices
  if (!data.advices || typeof data.advices !== "object") {
    data.advices = {};
    warn("advices", "was missing, created as empty object");
  }

  // medications_to_continue → array of objects
  if (!Array.isArray(data.advices.medications_to_continue)) {
    data.advices.medications_to_continue = ensureArray(data.advices.medications_to_continue);
    warn("advices.medications_to_continue", "was not an array, coerced");
  }
  data.advices.medications_to_continue = data.advices.medications_to_continue.map(fixMedicationItem);

  // diet
  if (!data.advices.diet || typeof data.advices.diet !== "object") {
    data.advices.diet = { type: "", recommendations: [] };
    warn("advices.diet", "was missing, created with defaults");
  }
  fixStr(data.advices.diet, "type", "advices.diet.type");
  if (!Array.isArray(data.advices.diet.recommendations)) {
    data.advices.diet.recommendations = ensureArray(data.advices.diet.recommendations);
    warn("advices.diet.recommendations", "was not an array, coerced");
  }
  data.advices.diet.recommendations = stripBlanks(data.advices.diet.recommendations);

  // activity_restrictions, warning_signs → arrays
  for (const key of ["activity_restrictions", "warning_signs"]) {
    if (!Array.isArray(data.advices[key])) {
      data.advices[key] = ensureArray(data.advices[key]);
      warn(`advices.${key}`, "was not an array, coerced");
    }
    data.advices[key] = stripBlanks(data.advices[key]);
  }

  // follow_up
  data.advices.follow_up = ensureObject(data.advices.follow_up, {
    department: "",
    after_days: null,
    purpose: "",
  });
  for (const key of ["department", "purpose"]) {
    fixStr(data.advices.follow_up, key, `advices.follow_up.${key}`);
  }
  // after_days must be an integer (or null)
  const rawDays = data.advices.follow_up.after_days;
  if (rawDays === "" || isNil(rawDays)) {
    data.advices.follow_up.after_days = null;
  } else {
    const parsed = parseInt(rawDays, 10);
    data.advices.follow_up.after_days = isNaN(parsed) ? null : parsed;
    if (isNaN(parsed)) {
      warn("advices.follow_up.after_days", `"${rawDays}" is not a valid integer, set to null`);
    }
  }

  // 8. referral_details
  if (!data.referral_details || typeof data.referral_details !== "object") {
    data.referral_details = {};
    warn("referral_details", "was missing, created as empty object");
  }
  for (const key of ["referred_by", "reason_for_referral", "referral_date"]) {
    fixStr(data.referral_details, key, `referral_details.${key}`);
  }

  // 9. prepared_by / checked_by
  for (const section of ["prepared_by", "checked_by"]) {
    if (!data[section] || typeof data[section] !== "object") {
      data[section] = {};
      warn(section, "was missing, created as empty object");
    }
    // Ensure all existing keys are strings
    for (const key of Object.keys(data[section])) {
      fixStr(data[section], key, `${section}.${key}`);
    }
  }

  return {
    valid: true,
    data,
    warnings,
  };
};

export default validateDischargePayload;
